// src/components/Experiences.js
import React, { useState, useEffect, useRef } from 'react'
import { Container, Row, Col, Button, Accordion, Alert } from 'react-bootstrap'
import { useNavigate, useLocation } from 'react-router-dom'
 
import FloatingActionButton from './FloatingActionButton'
import { useContent } from '../hooks/useContent'
import Footer from './footer'
import './Experiences.css'
import hike from '../assets/imgDisp/hike.jpg'
import farm from '../assets/imgDisp/farmHero.jpg'
import fire from '../assets/imgDisp/fire.jpg'
import trail from '../assets/imgDisp/trials.jpg'
import bike from '../assets/imgDisp/bike.jpg'
import campFire from '../assets/imgDisp/campFire.jpg'
import birdwatch from '../assets/imgDisp/bird.jpg'
import little from '../assets/imgDisp/kid.jpg'
import indoor from '../assets/imgDisp/crick.jpg'
import simply from '../assets/imgDisp/simply.jpg'
import yoga from '../assets/imgDisp/outdoor.jpg'
import googleLogo from '../assets/google.png'
import tripadvisorLogo from '../assets/tripadv.png'


const Experiences = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { content, loading, error } = useContent('experiencesPage')

  // Extract content with fallbacks
  const heroContent = content?.hero || {
    title: 'Discover Experiences',
    subtitle: 'What You Can Do Here',
    backgroundImage: '',
  }
  const introContent = content?.intro || {
    title: 'Discover Your Perfect Experience',
    description: '',
    stats: [],
  }
  const experiencesContent = content?.experiences || []
  const testimonialsContent = content?.testimonialsSection || {
    title: 'Guest Experiences',
    testimonials: [],
  }
  const ctaContent = content?.cta || {
    title: 'Begin Your Restoration Journey',
    description: '',
    primaryButtonText: 'Consult with Guides',
    secondaryButtonText: 'View All Packages',
  }

  const [experienceImages, setExperienceImages] = useState([
    // On-Property Hikes
    hike,
    // Farm and Garden
    farm,
    // Fireside Conversations
    fire,
    // Nature Trails & Hikes
    trail,
    // Mountain & Road Biking
    bike,
    // Outdoor Fitness & Yoga
    yoga,
    // Stargazing & Campfires
    campFire,
    // Birdwatching & Wildlife
    birdwatch,
    // For Little Explorers
    little,
    // Indoor Sports & Play
    indoor,
    // Spaces to Simply Be
    simply,
  ])
  
  // Lazy background helper to defer heavy hero image until near viewport
  const LazyBackground = ({ className, src, style, children }) => {
    const ref = useRef(null)
    const [loaded, setLoaded] = useState(false)

    useEffect(() => {
      const el = ref.current
      if (!el || !src) {
        setLoaded(true)
        return
      }
      if (typeof IntersectionObserver === 'undefined') {
        setLoaded(true)
        return
      }
      const io = new IntersectionObserver(
        entries => {
          for (const entry of entries) {
            if (entry.isIntersecting) {
              setLoaded(true)
              io.disconnect()
              break
            }
          }
        },
        { rootMargin: '200px 0px', threshold: 0.01 }
      )
      io.observe(el)
      return () => io.disconnect()
    }, [src])

    const bg = loaded && src ? { backgroundImage: `url(${src})` } : {}
    return (
      <div ref={ref} className={className} style={{ ...(style || {}), ...bg }}>
        {children}
      </div>
    )
  }

  // Handle section navigation from URL params
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search)
    const targetSection = urlParams.get('section')

    

    if (targetSection && targetSection !== 'undefined') {
      // Wait for component to mount and accordion to render, then scroll to section
      setTimeout(() => {
        const element = document.getElementById(targetSection)
        if (element) {
          

          // Since accordion is alwaysOpen, we don't need to expand it
          // Just scroll directly to the section with offset for navbar
          const yOffset = -100 // Offset for fixed navbar
          const elementTop = element.getBoundingClientRect().top
          const scrollPosition = window.pageYOffset + elementTop + yOffset
          
          window.scrollTo({ 
            top: scrollPosition, 
            behavior: 'smooth' 
          })
        } else {
          
        }
      }, 1000) // Increased timeout to ensure accordion is fully rendered
    } else {
      // No section specified, scroll to top
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }, [location.search])

  // Hardcoded default experiences (used as fallback when CMS has none)
  const defaultExperiences = [
    {
      title: 'On-Property Hikes',
      description:
        'There are miles of hiking trails through these wooded mountains, some gentle, others more vigorous. Some trails climb up to 7000+ feet for views of mountain tops that roll on forever, while others wander through mature stands of dense forests with carpets of grasslands and wildflowers.',
      benefits: [
        'Trails ranging from gentle to vigorous',
        'Mountain top views at 7000+ feet',
        'Dense forest exploration',
        'Wildflower meadows and grasslands',
      ],
      duration: 'Half day to full day',
      icon: '🥾',
      category: 'Outdoor Adventure',
      sectionId: 'on-property-hikes',
    },
    {
      title: 'Farm and Garden',
      description:
        'A walk from our main lodging area, guests can visit our Farmstead. Here you can meet our chickens, peruse our vegetable garden, pay a visit to our cows, walk amongst our fruit trees, and get a taste for the farming traditions that make Ampthilldowns so unique and experience the rhythms and rewards of sustainable farming.',
      benefits: [
        'Meet farm animals (chickens, cows)',
        'Explore vegetable gardens',
        'Walk through fruit orchards',
        'Learn sustainable farming practices',
      ],
      duration: '1-2 hours',
      icon: '🚜',
      category: 'Farm Experience',
      sectionId: 'farm-and-garden',
    },
    {
      title: 'Fireside Conversations',
      description:
        "Evenings at Ampthilldowns often end around a glowing fire—where stories are shared, silences are savoured, and time gently slows down. Whether you're stargazing, sipping something warm, or just enjoying the crackle and calm, our fireside moments are where strangers become friends, and memories take root.",
      benefits: [
        'Storytelling and conversation',
        'Stargazing opportunities',
        'Warm beverages by the fire',
        'Community building experiences',
      ],
      duration: 'Evening sessions',
      icon: '🔥',
      category: 'Social Connection',
      sectionId: 'fireside-conversations',
    },
    {
      title: 'Nature Trails & Hikes',
      description:
        'Explore miles of scenic trails winding through the hills, ranging from gentle strolls to more challenging hikes. Wander through ancient woods, lush fern carpets, and fields dotted with wildflowers. Each path leads to a new perspective—and some, to breathtaking mountaintop views.',
      benefits: [
        'Scenic trail exploration',
        'Ancient woodland walks',
        'Wildflower field discovery',
        'Breathtaking mountain views',
      ],
      duration: '1-4 hours',
      icon: '🌲',
      category: 'Nature Exploration',
      sectionId: 'nature-trails',
    },
    {
      title: 'Mountain & Road Biking',
      description:
        'Set off on an exhilarating mountain biking adventure through wooded trails and hilly terrain or cycle through our rugged off-road trails or take the scenic route along winding country roads on a road bike. Whichever path you choose, the surrounding landscapes are sure to inspire.',
      benefits: [
        'Mountain biking trails',
        'Off-road cycling paths',
        'Scenic country road routes',
        'Equipment available on request',
      ],
      duration: '2-4 hours',
      icon: '🚴',
      category: 'Adventure Sports',
      sectionId: 'mountain-biking',
    },
    {
      title: 'Outdoor Fitness & Yoga in the Wild',
      description:
        'Make the outdoors your personal wellness studio. We provide yoga mats, blocks, and fitness accessories on request so you can stretch, move, and breathe surrounded by the tranquillity of nature—be it under a tree, by the stream, or on a quiet meadow.',
      benefits: [
        'Yoga mats and equipment provided',
        'Multiple outdoor locations',
        'Personalized fitness sessions',
        'Nature-immersed wellness',
      ],
      duration: '1-2 hours',
      icon: '🧘',
      category: 'Wellness',
      sectionId: 'yoga-fitness',
    },
    {
      title: 'Stargazing & Campfires',
      description:
        'With zero light pollution, our stargazing deck offers crystal-clear views of the night sky. Whether you bring a telescope or just lie back and look up, the celestial display here is nothing short of awe-inspiring.',
      benefits: [
        'Zero light pollution environment',
        'Crystal-clear night sky views',
        'Telescope-friendly setup',
        'Campfire experiences',
      ],
      duration: 'Evening sessions',
      icon: '⭐',
      category: 'Astronomy',
      sectionId: 'stargazing-campfires',
    },
    {
      title: 'Birdwatching & Wildlife Encounters',
      description:
        'Our biodiverse surroundings are home to an array of native birdlife and wildlife. Early mornings and early evenings are especially magical, where patient observers may spot bison, deer, peacocks, boars moving quietly across the distant landscape.',
      benefits: [
        'Native birdlife observation',
        'Wildlife spotting opportunities',
        'Early morning/evening sessions',
        'Biodiverse ecosystem exploration',
      ],
      duration: '1-3 hours',
      icon: '🦅',
      category: 'Wildlife',
      sectionId: 'birdwatching-wildlife',
    },
    {
      title: 'For Little Explorers',
      description:
        'At Ampthilldowns, every tree, trail, and puddle hold a story waiting to be discovered. From feeding farm animals and spotting butterflies to helping in the garden or finding shapes in clouds—children reconnect with the natural world, free from screens and full of wonder. Here, curiosity is encouraged and adventure begins just beyond the doorstep.',
      benefits: [
        'Farm animal interactions',
        'Butterfly and nature spotting',
        'Garden participation',
        'Screen-free exploration',
      ],
      duration: 'Flexible',
      icon: '👶',
      category: 'Family',
      sectionId: 'little-explorers',
    },
    {
      title: 'Indoor Sports & Play',
      description:
        "At Ampthill Downs, fun doesn't stop when you step indoors. Our indoor play spaces are designed for guests of all ages to relax, bond, and enjoy active leisure. From classic games like table tennis, carrom, and chess to cozy corners for board games and group play, there's something for everyone.",
      benefits: [
        'Table tennis and carrom',
        'Chess and board games',
        'Group play areas',
        'All-weather entertainment',
      ],
      duration: 'Flexible',
      icon: '🎮',
      category: 'Indoor Activities',
      sectionId: 'indoor-sports',
    },
    {
      title: 'Spaces to Simply Be',
      description:
        "Amidst all the activity lies our most cherished offering: silence and space, a place to simply be. Set up an art easel, sip tea by the bonfire, nap in a hammock beneath a tree, or lose yourself in the pages of a book. Here, stillness is not empty—it's full of presence.",
      benefits: [
        'Art and creative spaces',
        'Peaceful reading corners',
        'Hammock relaxation areas',
        'Mindful presence practice',
      ],
      duration: 'Flexible',
      icon: '☁️',
      category: 'Mindfulness',
      sectionId: 'spaces-to-be',
    },
  ]

  // Helper to generate a section id slug from a title
  const slugify = (str) =>
    (str || '')
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')

  // Build experiences list from CMS if present; otherwise use defaults
  const experiences = (content?.experiences && content.experiences.length)
    ? content.experiences.map((exp, idx) => ({
        title: exp.title || `Experience ${idx + 1}`,
        description: exp.description || '',
        benefits: exp.benefits || [], // Allow future extension
        duration: exp.duration || '',
        icon: exp.icon || '✨',
        category: exp.category || 'Experience',
        sectionId: exp.sectionId || slugify(exp.title) || `experience-${idx}`,
        image: exp.image || '',
      }))
    : defaultExperiences

  // Removed loading spinner: render page immediately with fallback content.

  // Show content error state
  if (error) {
    return (
      <div className='experiences-page'>
        <Alert variant='danger' className='text-center mb-0'>
          <strong>Content Loading Error:</strong> {error}
          <p className='mt-2'>Please try refreshing the page.</p>
        </Alert>
      </div>
    )
  }

  return (
    <div className='experiences-page' style={{ background: 'none', backgroundColor: 'transparent' }}>
      {/* Hero Section */}
      <section className='hero-section' style={{ background: 'none', backgroundColor: 'transparent' }}>
        {heroContent.backgroundImage && (
          <img 
            src={heroContent.backgroundImage} 
            alt="Hero" 
            className='hero-image'
            loading='eager'
            fetchpriority='high'
            decoding='async'
            style={{ position: 'absolute', width: '100%', height: '100%', objectFit: 'cover' }}
          />
        )}
        <div className='hero-overlay is-loaded'>
          <div className='hero-content'>
            <h1 className='hero-title'>{heroContent.title}</h1>
            <p className='hero-subtitle'>{heroContent.subtitle}</p>
          </div>
        </div>
      </section>

      {/* Introduction */}
      <section className='intro-section py-5'>
        <Container>
          <Row className='justify-content-center'>
            <Col md={10} className='text-center'>
              <h2 className='section-head'>{introContent.title}</h2>
              <p className='lead'>{introContent.description}</p>
              <div className='experience-stats mt-4'>
                {introContent.stats.map((stat, index) => (
                  <div key={index} className='stat-item'>
                    <div className='stat-label'>{stat.label}</div>
                  </div>
                ))}
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Experience Accordion */}
      <section className='experiences-section py-5 bg-light'>
        <Container>
          <div className='text-center mb-5'>
            <h2 className='section-head'>Our Experiences</h2>
            <p className='lead'>
              Choose your path to adventure, relaxation, and connection
            </p>
          </div>

          <Accordion defaultActiveKey={experiences.map((_, i) => i.toString())} alwaysOpen className='experience-accordion'>
            {experiences.map((exp, index) => (
              <Accordion.Item
                eventKey={index.toString()}
                key={index}
                id={exp.sectionId}
              >
                <Accordion.Header>
                  <div className='accordion-content'>
                    <div className='accordion-icon me-3'>{exp.icon}</div>
                    <div className='accordion-text'>
                      <h3>{exp.title}</h3>
                      <span className='category-badge'>{exp.category}</span>
                    </div>
                  </div>
                </Accordion.Header>
                <Accordion.Body>
                  <Row className='align-items-center'>
                    <Col
                      md={6}
                      className={index % 2 === 0 ? 'order-md-1' : 'order-md-2'}
                    >
                      <p className='experience-description'>
                        {exp.description}
                      </p>
                      <h4 className='benefits-title'>
                        What You'll Experience:
                      </h4>
                      <ul className='benefits-list'>
                        {exp.benefits.map((benefit, i) => (
                          <li key={i}>{benefit}</li>
                        ))}
                      </ul>
                      <div className='experience-meta'>
                      </div>
                    </Col>
                    <Col
                      md={6}
                      className={
                        index % 2 === 0
                          ? 'order-md-2 mb-4 mb-md-0'
                          : 'order-md-1 mb-4 mb-md-0'
                      }
                    >
                      <div className='simple-image-wrapper'>
                        <img
                          src={
                            exp.image ||
                            experienceImages[index] ||
                            experienceImages[0] ||
                            hike
                          }
                          alt={exp.title}
                          className='simple-experience-img'
                          loading='lazy'
                          decoding='async'
                          onError={(e) => {
                            e.target.src = experienceImages[index] || hike
                          }}
                        />
                        <div className='simple-overlay'>
                          <div className='overlay-emoji'>{exp.icon}</div>
                          <h4 className='overlay-heading'>{exp.title}</h4>
                          <p className='overlay-badge'>{exp.category}</p>
                        </div>
                      </div>
                    </Col>
                  </Row>
                </Accordion.Body>
              </Accordion.Item>
            ))}
          </Accordion>
        </Container>
      </section>

      {/* Testimonials */}
      <section className='testimonials-section py-5'>
        <Container>
          <h2 className='section-head'>{testimonialsContent.title}</h2>
          <Row className='g-4'>
            {testimonialsContent.testimonials.map((t, index) => {
              const name = t.author || 'Guest'
              const initials = name
                .split(' ')
                .filter(Boolean)
                .slice(0, 2)
                .map((s) => s[0]?.toUpperCase())
                .join('') || 'G'
              const rating = Math.max(0, Math.min(5, Number(t.rating ?? 5)))
              const source = String(t.source || '').toLowerCase()
              const sourceLogo = source.includes('google')
                ? googleLogo
                : source.includes('trip')
                ? tripadvisorLogo
                : null

              return (
                <Col md={4} key={index}>
                  <div className='exp-testimonial-card p-4 h-100 d-flex flex-column'>
                    <div className='d-flex justify-content-between align-items-start mb-3'>
                      <div className='d-flex align-items-center'>
                        <div className='exp-avatar-circle me-3'>
                          <span>{initials}</span>
                        </div>
                        <div>
                          <h5 className='exp-author-name mb-0'>{name}</h5>
                          <small className='exp-author-loc text-muted'>
                            {t.location || ''}
                          </small>
                        </div>
                      </div>
                      {sourceLogo && (
                        <div className='exp-review-source-logo'>
                          <img src={sourceLogo} alt='Source' />
                        </div>
                      )}
                    </div>

                    <div className='exp-star-rating mb-3'>
                      {Array.from({ length: rating }).map((_, i) => (
                        <span key={`full-${i}`} className='exp-star'>★</span>
                      ))}
                      {Array.from({ length: 5 - rating }).map((_, i) => (
                        <span key={`empty-${i}`} className='exp-star empty'>★</span>
                      ))}
                    </div>

                    <p className='exp-testimonial-quote flex-grow-1'>
                      “{t.text}”
                    </p>
                  </div>
                </Col>
              )
            })}
          </Row>
        </Container>
      </section>

      {/* Call to Action */}
      <section className='sectionCta py-5 text-center'>
        <Container>
          <h2 className='section-head'>{ctaContent.title}</h2>
          <p className='cta-text mb-4'>{ctaContent.description}</p>
          <button
            onClick={() => navigate('/contact')}
            variant='primary'
            // size='lg'
            className='experiences-book-btn'
          >
            {ctaContent.primaryButtonText || 'Consult with Guides'}
          </button>
          {/* <button
            onClick={() => navigate('/stays')}
            variant='outline-light'
            size='lg'
            className='book-btn'
          >
            {ctaContent.secondaryButtonText}
          </button> */}
        </Container>
      </section>

      {/* Footer */}
   <Footer />

      <FloatingActionButton />
    </div>
  )
}

export default Experiences
